/**
 * RankUpModal.js — Iter. 411.g
 *
 * Small motivational modal that fires on the Dashboard when the user's
 * weekly leaderboard rank improves since the last visit. Uses the
 * :func:`createPortal` pattern (Iter. 406.dd / 411.d) to escape any
 * ancestor `transform`/`filter` stacking context — otherwise the card
 * gets trapped and only the backdrop is visible.
 *
 * Trigger contract:
 *   1. Dashboard mounts.
 *   2. GET /api/gamification/rank-up/pending
 *   3. If `has_rank_up`, show this modal.
 *   4. On dismiss, POST /api/gamification/rank-up/acknowledge so the
 *      SAME rank improvement is not re-shown on every reload.
 *
 * NOT gated by role — Dashboard already excludes staff-only paths
 * elsewhere, and the endpoint uses the shared /leaderboard/relative
 * plumbing which returns null user_rank for users not in the ladder.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Trophy, ArrowUp, X, Sparkles } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';


export default function RankUpModal({ userId }) {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [payload, setPayload] = useState(null);
  const [visible, setVisible] = useState(false);

  // Iter. 411.mm17 — HARD-STOP loop fix. Prod user reported "the X doesn't
  // close it, I have to kill the app; when I come back and try to log a
  // meal, it reappears → infinite loop".
  //
  // Root causes we harden against:
  //   (a) POST /acknowledge fails silently (network/500) → next Dashboard
  //       mount fetches /pending → still returns has_rank_up=true → modal
  //       fires again forever until the ack lands. Fix: also stash the
  //       acknowledged rank in localStorage so the frontend refuses to
  //       show the SAME rank a second time even if the backend forgot.
  //   (b) User clicks X before the 500 ms `setTimeout(setVisible(true))`
  //       fires → the timer wins the race and re-shows the modal after
  //       dismiss. Fix: track the timer id and clear it on dismiss.
  //   (c) `payload` remained set after dismiss → any state ricochet that
  //       flipped `visible` back to true would re-render the modal. Fix:
  //       null out payload on dismiss.
  const showTimerRef = useRef(null);
  const LS_KEY = 'yumix_rank_up_seen_v1';

  const readSeen = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); }
    catch { return null; }
  }, []);
  const writeSeen = useCallback((rank) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        user_id: userId, rank, at: Date.now(),
      }));
    } catch { /* private mode, ignore */ }
  }, [userId]);

  // Fetch once on mount + on user change. Silent on any failure — this
  // is a delight surface, never blocks the Dashboard.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/gamification/rank-up/pending');
        if (cancelled) return;
        if (res.data?.has_rank_up) {
          // Local dedupe: if the frontend has already acknowledged this
          // exact rank (backend ack may have failed on prod), do NOT show.
          const seen = readSeen();
          if (seen && seen.user_id === userId && seen.rank === res.data.current_rank) {
            // Fire-and-forget re-ack so the server catches up eventually.
            api.post('/gamification/rank-up/acknowledge').catch(() => {});
            return;
          }
          setPayload(res.data);
          // Small delay so the Dashboard's own paint completes first — the
          // modal feels less abrupt when it fades in after ~500ms.
          showTimerRef.current = setTimeout(() => {
            if (!cancelled) setVisible(true);
          }, 500);
        } else if (res.data?.current_rank != null) {
          // No rank-up but user IS on the ladder — silently seed the
          // baseline so the FIRST future improvement fires the modal.
          await api.post('/gamification/rank-up/acknowledge').catch(() => {});
        }
      } catch {
        // silent
      }
    })();
    return () => {
      cancelled = true;
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [userId, readSeen]);

  const dismiss = useCallback(async () => {
    // Cancel the pending show-timer immediately (race guard).
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    // Persist locally FIRST so a network failure on the ack POST doesn't
    // leave the frontend stuck in a re-show loop.
    if (payload?.current_rank != null) writeSeen(payload.current_rank);
    // Kill both state slots so ANY ricochet that flips `visible` back to
    // true still short-circuits at the `!payload` guard.
    setVisible(false);
    setPayload(null);
    try { await api.post('/gamification/rank-up/acknowledge'); } catch { /* silent */ }
  }, [payload, writeSeen]);

  if (!visible || !payload) return null;

  const { previous_rank, current_rank, delta, current_points, total_players, team_id } = payload;
  const teamTone = team_id === 'bleu' ? 'from-sky-500/25 to-blue-600/10' : 'from-red-500/25 to-orange-600/10';
  const teamBorder = team_id === 'bleu' ? 'border-sky-400/50' : 'border-red-400/50';
  const teamGlow = team_id === 'bleu' ? 'shadow-sky-500/25' : 'shadow-red-500/25';

  const headline = fr ? 'TU GRIMPES !' : 'YOU CLIMB!';
  const subline = fr
    ? `Tu passes de #${previous_rank} à #${current_rank} — ${delta} rang${delta > 1 ? 's' : ''} gagné${delta > 1 ? 's' : ''}.`
    : `From #${previous_rank} up to #${current_rank} — ${delta} spot${delta > 1 ? 's' : ''} climbed.`;
  const punch = fr
    ? 'Continue comme ça. Chaque repas, chaque entraînement compte.'
    : 'Keep going. Every meal, every workout counts.';

  const overlay = (
    <div
      className="fixed inset-0 z-[130] bg-black/75 backdrop-blur-md flex items-center justify-center p-4"
      style={{ height: '100dvh' }}
      onClick={dismiss}
      data-testid="rank-up-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-sm rounded-3xl bg-gradient-to-br ${teamTone} border ${teamBorder} shadow-2xl ${teamGlow} p-6 pt-8`}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-colors"
          aria-label={fr ? 'Fermer' : 'Close'}
          data-testid="rank-up-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Trophy + delta pill */}
        <div className="flex flex-col items-center gap-3">
          <motion.div
            initial={{ scale: 0.6, rotate: -12 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 300 }}
            className="relative"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/40">
              <Trophy className="w-10 h-10 text-yellow-50" strokeWidth={2.2} />
            </div>
            <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-emerald-500 border-2 border-black flex items-center justify-center">
              <ArrowUp className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
          </motion.div>

          <div className="flex items-center gap-1 text-yellow-300 text-[11px] uppercase tracking-widest font-bold">
            <Sparkles className="w-3 h-3" />
            {fr ? 'Classement Tactique' : 'Tactical Ranking'}
            <Sparkles className="w-3 h-3" />
          </div>

          <h2
            className="text-3xl font-heading font-black text-center text-white tracking-tight leading-none"
            data-testid="rank-up-modal-headline"
          >
            {headline}
          </h2>

          <p
            className="text-center text-white/90 text-sm leading-snug font-semibold"
            data-testid="rank-up-modal-subline"
          >
            {subline}
          </p>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 w-full mt-2">
            <div className="rounded-xl bg-black/30 border border-white/10 py-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-white/60">{fr ? 'Rang' : 'Rank'}</p>
              <p className="text-lg font-bold text-white">#{current_rank}</p>
            </div>
            <div className="rounded-xl bg-black/30 border border-white/10 py-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-white/60">{fr ? 'Points' : 'Points'}</p>
              <p className="text-lg font-bold text-white">{current_points}</p>
            </div>
            <div className="rounded-xl bg-black/30 border border-white/10 py-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-white/60">{fr ? 'Sur' : 'Of'}</p>
              <p className="text-lg font-bold text-white">{total_players}</p>
            </div>
          </div>

          <p className="text-center text-white/70 text-xs italic mt-1 px-2">{punch}</p>

          <button
            type="button"
            onClick={dismiss}
            className="w-full mt-3 py-3 rounded-full bg-white text-black font-black uppercase tracking-widest text-xs hover:bg-white/90 active:scale-95 transition-all shadow-lg"
            data-testid="rank-up-modal-cta"
          >
            {fr ? 'En avant, soldat !' : 'March on, soldier!'}
          </button>
        </div>
      </motion.div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(overlay, document.body);
}
